<template>
  <div>
    <h3 class="pt-3">{{ title }}</h3>
    <p>{{ introductionText }}</p>
    <b-card no-body class="mt-3">
      <b-tabs ref="tabs" v-model="currentPage" card>
        <b-tab
          ref="info"
          :title="$t('complaint.information')"
          :disabled="complaintCreated || uploading"
        >
          <p>{{ instructionText }}</p>
          <hr />
          <b-form no-validate>
            <b-row>
              <b-col cols="12">
                <b-form-group>
                  {{ $t("complaint.fields.anonymous") }}
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="
                      complaint.anonymous ? 'danger' : 'secondary'
                    "
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="complaint.anonymous"
                    class="mb-auto ml-1"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="complaint.anonymous ? 'secondary' : 'info'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.fullName">
                  <b-form-input
                    v-model="complaint.denouncer.fullName"
                    v-validate="rules.denouncer.fullName"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidFullName"
                    name="fullName"
                    :state="validateState('fullName')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidFullName">
                    {{ errors.first("fullName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!complaint.anonymous">
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.phone">
                  <b-form-input
                    ref="phone"
                    v-model="complaint.denouncer.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.denouncer.phone"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :state="validateState('phone')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.email">
                  <b-form-input
                    v-model="complaint.denouncer.email"
                    v-validate="rules.denouncer.email"
                    type="email"
                    class="form-control"
                    aria-describedby="invalidEmail"
                    name="email"
                    :state="validateState('email')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidEmail">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="6">
                <b-form-group>
                  {{ $t("complaint.fields.previousProtocol.desc") }}
                  <b-form-checkbox
                    v-model="hasPreviousProtocol"
                    class="mb-auto"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="
                      hasPreviousProtocol ? 'secondary' : 'danger'
                    "
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="hasPreviousProtocol"
                    class="mb-auto ml-1"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="hasPreviousProtocol ? 'info' : 'secondary'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" lg="3">
                <b-form-group
                  v-if="hasPreviousProtocol"
                  :label="i18n.previousProtocol"
                >
                  <b-form-input
                    v-model="complaint.previousProtocol"
                    v-validate="rules.previousProtocol"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPreviousProtocol"
                    name="previousProtocol"
                    :state="validateState('previousProtocol')"
                  ></b-form-input>
                  <b-form-invalid-feedback id="invalidPreviousProtocol">
                    {{ errors.first("previousProtocol") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="natureEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group label="Qual o tema principal da sua denúncia?">
                  <b-form-select
                    v-model="complaint.classification.nature"
                    v-validate="{ required: true }"
                    :options="classificationOptions.nature"
                    aria-describedby="invalid-nature"
                    name="nature"
                    :state="validateState('nature')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-nature">
                    {{ errors.first("nature") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="originEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group label="A situação que deseja denunciar ocorreu">
                  <b-form-select
                    v-model="complaint.classification.origin"
                    v-validate="{ required: true }"
                    :options="classificationOptions.origin"
                    aria-describedby="invalid-origin"
                    name="origin"
                    :state="validateState('origin')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-origin">
                    {{ errors.first("origin") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="reportingAgentEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group
                  :label="`Qual sua relação com a ${company.fantasyName}?`"
                >
                  <b-form-select
                    v-model="complaint.classification.reportingAgent"
                    v-validate="{ required: true }"
                    :options="classificationOptions.reportingAgent"
                    aria-describedby="invalid-reportingAgent"
                    name="reportingAgent"
                    :state="validateState('reportingAgent')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-reportingAgent">
                    {{ errors.first("reportingAgent") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="businessUnitEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group label="Selecione o local onde a situação ocorreu">
                  <b-form-select
                    v-model="complaint.classification.businessUnit"
                    v-validate="{ required: true }"
                    :options="classificationOptions.businessUnit"
                    aria-describedby="invalid-businessUnit"
                    name="businessUnit"
                    :state="validateState('businessUnit')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-businessUnit">
                    {{ errors.first("businessUnit") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="involvedDepartmentEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group label="Selecione o setor onde a situação ocorreu">
                  <b-form-select
                    v-model="complaint.classification.involvedDepartment"
                    v-validate="{ required: true }"
                    :options="classificationOptions.involvedDepartment"
                    aria-describedby="invalid-involvedDepartment"
                    name="involvedDepartment"
                    :state="validateState('involvedDepartment')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-involvedDepartment">
                    {{ errors.first("involvedDepartment") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Forneça a data específica ou aproximada em que o incidente ocorreu:"
                  description="(Ex.: terça, 3 de maio de 2002; duas semanas atrás; aproximadamente 1 mês)"
                >
                  <b-form-textarea
                    v-model="complaint.date"
                    v-validate="rules.date"
                    class="form-control"
                    aria-describedby="invalidDate"
                    name="complaintDate"
                    :rows="2"
                    :state="validateState('complaintDate')"
                  ></b-form-textarea>
                  <b-form-invalid-feedback id="invalidDate">
                    {{ errors.first("complaintDate") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group label="Descreva o(s) fato(s) ocorrido(s)">
                  <span class="text-danger">ATENÇÃO</span> Esse campo deve ser
                  preenchido da maneira mais completa e detalhada possível.
                  <b-form-textarea
                    v-model="complaint.text"
                    v-validate="rules.text"
                    class="form-control"
                    aria-describedby="invalidText"
                    name="complaintText"
                    :rows="4"
                    :state="validateState('complaintText')"
                  ></b-form-textarea>
                  <small class="form-text text-muted">
                    a) Quem fez? Nome completo, local de trabalho, empresa para
                    qual trabalha.<br />
                    b) O que a pessoa fez? O que a pessoa disse? Qual foi a
                    irregularidade, qual frase exata ela disse, qual
                    comportamento ocorreu.<br />
                    O envio de provas, evidências ou indícios da irregularidade
                    também facilita a apuração da denúncia.
                  </small>
                  <b-form-invalid-feedback id="invalidText">
                    {{ errors.first("complaintText") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="channelConfidenceEnabled">
              <b-col cols="12" lg="4" md="6">
                <b-form-group
                  label="Qual o seu nível de confiança neste canal?"
                >
                  <b-form-select
                    v-model="complaint.classification.channelConfidence"
                    v-validate="{ required: true }"
                    :options="classificationOptions.channelConfidence"
                    aria-describedby="invalid-channelConfidence"
                    name="channelConfidence"
                    :state="validateState('channelConfidence')"
                  ></b-form-select>
                  <b-form-invalid-feedback id="invalid-channelConfidence">
                    {{ errors.first("channelConfidence") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab
          :title="$t('complaint.files')"
          :disabled="(!infoValidated || complaintCreated) && !uploading"
        >
          <h5 class="pt-1 pb-1">{{ $t("complaint.filesInstruction") }}</h5>
          <multiple-file-upload
            ref="upload"
            :post-action="upload.postAction"
            :data="upload.data"
            :disabled="uploading || uploadComplete"
            @upload-complete="onUploadComplete"
          ></multiple-file-upload>
        </b-tab>
        <b-tab
          :title="$t('complaint.confirmation')"
          :disabled="!infoValidated || uploading"
        >
          <template v-if="complaintCreated">
            <h3>{{ $t("complaint.success") }}</h3>
            <h5>{{ $t("complaint.successInfo") }}</h5>
            <h5>
              {{ $t("labels.protocol") }}:
              <strong>{{ context.protocol }}</strong>
            </h5>
          </template>

          <template v-else>
            <p>{{ $t("complaint.termsInfo") }}</p>
            <b-form-group>
              <b-form-checkbox
                v-model="complaint.termsAccepted"
                v-validate="rules.termsAccepted"
                class="mb-auto"
                style="white-space: pre-line"
                :value="true"
                :unchecked-value="false"
                name="termsAccepted"
                :state="validateState('termsAccepted')"
                >{{ $t("complaint.fields.termsAccepted") }}
              </b-form-checkbox>
            </b-form-group>
            <b-form-group v-if="!complaint.anonymous">
              <b-form-checkbox
                v-model="complaint.dataConsentAccepted"
                v-validate="rules.dataConsentAccepted"
                class="mb-auto"
                :value="true"
                :unchecked-value="false"
                name="dataConsentAccepted"
                :state="validateState('dataConsentAccepted')"
                >{{ $t("complaint.fields.dataConsentAccepted") }}
              </b-form-checkbox>
            </b-form-group>
          </template>
        </b-tab>
      </b-tabs>
      <template slot="footer">
        <template v-if="complaintCreated">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="$router.replace('/')"
            ><i class="fas fa-home" aria-hidden="true"></i> Início
          </b-button>
        </template>
        <template v-else>
          <b-button class="mr-1" :disabled="loading" @click="previousPage">{{
            $t("complaint.back")
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="
              loading || (currentPage === 1 && $refs.upload.hasInvalidFiles)
            "
            @click="nextPage"
          >
            {{
              currentPage === 2 ? $t("complaint.finish") : $t("complaint.next")
            }}
          </b-button>
        </template>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ValidatorDictionary } from "@/i18n";
import { mask } from "vue-the-mask";
import MultipleFileUpload from "../../../components/MultipleFileUpload";
import { sendComplaint } from "@/api/main";
import typologyValues from "../../../../../staticData/typology.json";

export default {
  name: "CompanyComplaint",
  directives: { mask },
  components: { MultipleFileUpload },
  data() {
    return {
      hasPreviousProtocol: false,
      complaint: {
        termsAccepted: false,
        dataConsentAccepted: false,
        anonymous: true,
        denouncer: {},
        classification: {},
      },
      currentPage: 0,
      totalPages: 3,
      infoValidated: false,
      loading: false,
      uploading: false,
      uploadComplete: false,
      upload: {
        postAction: "",
        data: {},
      },
      context: {
        protocol: null,
      },
      complaintCreated: false,
    };
  },
  computed: {
    ...mapGetters([
      "company",
      "portal",
      "classificationOptions",
      "enabledClassificationOptions",
    ]),
    title() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.title
      )
        return portal.languages[lang].complaint.title;
      return this.$i18n.t("complaint.title");
    },
    introductionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.introductionText
      )
        return portal.languages[lang].complaint.introductionText;
      return this.$i18n.t("complaint.introduction", [this.company.fantasyName]);
    },
    instructionText() {
      const lang = this.$i18n.locale;
      const portal = this.portal;
      if (
        portal &&
        portal.languages &&
        portal.languages[lang] &&
        portal.languages[lang].complaint &&
        portal.languages[lang].complaint.instructionText
      )
        return portal.languages[lang].complaint.instructionText;
      return this.$i18n.t("complaint.instruction");
    },
    rules() {
      return {
        denouncer: {
          fullName: { required: !this.complaint.anonymous },
          position: { required: false },
          email: {
            required: !this.complaint.anonymous,
            email: true,
          },
          phone: {
            required: !this.complaint.anonymous,
          },
        },
        previousProtocol: { required: this.hasPreviousProtocol },
        text: { required: true },
        date: { required: true },
        location: { required: false },
        knowAbout: { required: false },
        witnesses: { required: false },
        financialInfo: { required: false },
        termsAccepted: { required: true, acceptance: true },
        dataConsentAccepted: {
          required: !this.complaint.anonymous,
          acceptance: true,
        },
      };
    },

    reportingAgentEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "reportingAgent"
      );
    },
    businessUnitEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "businessUnit"
      );
    },
    involvedDepartmentEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "involvedDepartment"
      );
    },
    channelConfidenceEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "channelConfidence"
      );
    },
    natureEnabled() {
      console.log(this.enabledClassificationOptions);
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "nature"
      );
    },
    originEnabled() {
      return this.enabledClassificationOptions.some(
        (opt) => opt.name === "origin"
      );
    },

    i18n() {
      return ValidatorDictionary[this.$i18n.locale].attributes;
    },
  },

  created() {
    const updateEls = ["phone"];
    this.$nextTick(() => {
      for (const id of updateEls) {
        this.$refs[id]?.$el.oninput({ isTrusted: true });
      }
      this.$validator.reset();
    });
  },

  methods: {
    sanitizePayload(payload) {
      if (payload.denouncer.phone) {
        payload.denouncer.phone = payload.denouncer.phone.replace(/[^\d]/g, "");
        if (!payload.denouncer.phone) delete payload.denouncer.phone;
      }
      if (payload.denouncer.cpf) {
        payload.denouncer.cpf = payload.denouncer.cpf.replace(/[^\d]/g, "");
        if (!payload.denouncer.cpf) delete payload.denouncer.cpf;
      }
    },
    async submitComplaint() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.complaint));
        this.sanitizePayload(payload);

        const response = await sendComplaint(this.portal.slug, payload);
        if (response.status === 201) {
          const { protocol } = response.data;
          const files = this.$refs.upload.selectedFiles();
          this.context = {
            protocol,
          };
          if (files.length > 0) {
            this.uploading = true;
            this.currentPage = 1;

            this.upload.postAction = `/api/complaints/${this.portal.slug}/${protocol}/files`;
            for (const file of files) {
              file.postAction = this.upload.postAction;
              file.headers = this.upload.headers;
            }

            this.$nextTick(() => this.$refs.upload.start());
          } else {
            this.onComplaintCreated(protocol);
          }
        }
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$notify({
            group: "app",
            text: error.response.data,
            type: "danger",
          });
        } else {
          this.$notify({
            group: "app",
            text: error.response.statusText,
            type: "danger",
          });
        }
      }
    },

    async onUploadComplete() {
      this.uploadComplete = true;
      this.uploading = false;
      this.$nextTick(() => {
        this.currentPage = 2;
        this.$nextTick(() => {
          this.onComplaintCreated(this.context.protocol);
        });
      });
    },

    onComplaintCreated() {
      this.$notify({
        group: "app",
        text: this.$i18n.t("complaint.success"),
        type: "success",
      });
      this.loading = false;
      this.complaintCreated = true;
    },
    async nextPage() {
      const next = () => {
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = 0;
        }
      };

      if (this.currentPage === 0) {
        let valid = await this.$validator.validateAll();
        const ignoreFields = ["termsAccepted"];
        if (!this.complaint.anonymous) {
          ignoreFields.push("dataConsentAccepted");
        }
        const errors = this.errors.items.filter(
          (item) => ignoreFields.indexOf(item.field) === -1
        );
        valid = valid || errors.length === 0;
        this.infoValidated = valid;
        if (valid) {
          this.$nextTick(next);
        }
      } else if (this.currentPage === 1) {
        if (!this.$refs.upload.hasInvalidFiles) {
          next();
        }
      } else if (this.currentPage === 2) {
        if (await this.$validator.validateAll()) {
          await this.submitComplaint();
        }
      } else {
        next();
      }
    },
    previousPage() {
      this.currentPage--;
      if (this.currentPage < 0) {
        this.currentPage = this.totalPages - 1;
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
